import { useTranslation } from 'react-i18next';
import { PMO_FLOW_CLASS_COLORS, waitMedianRatio } from './segment-utils.js';
import type { CSSProperties } from 'react';
import type { PmoFlowWaitTotal } from '../../../types/pmo-flow.js';

/** トラックの高さ。構成バーより細くして主従を付ける。 @graph-connects none */
const TRACK_HEIGHT_CLASS = 'h-2';

/** 中央値マーカーの幅（px）。 @graph-connects none */
const MARKER_WIDTH_PX = 2;

export interface WaitTimePanelProps {
  /** 案件あたりの待ち合計。算出できていなければ null */
  waitTotal: PmoFlowWaitTotal | null;
}

/**
 * 案件あたりの待ち合計を、0〜p90 のスケールで示すパネル。
 *
 * 構成バーには載せられない。実測で assign 待ちの p90（約 26 営業日）だけで
 * リードタイム中央値（21 営業日）を超えるため、同じ軸に p90 を描くと必ずはみ出す。
 * 待ちは待ち行列現象で右裾が長く、p50 と p90 を同じ尺で見せるには専用のスケールが要る。
 *
 * トラックは 0〜p90 で、中央値の位置にマーカーを立てる。マーカーが左に寄っているほど
 * 「たいていは短いが、たまに極端に待つ」形であることが読める。改善余地は尾の側にある。
 *
 * 件数とリードタイム占有率（`waitTotal.count` / `sharePercent`）は**表示しない**。
 * このパネルの母集団（待ち区間の境界が全て揃った案件）は母集団見出しの母票数より小さく、
 * 占有率も案件ごとの比率の中央値なので、隣に並ぶ見出しの中央値からは検算できない。
 * 数字が並んでいると割り算で辻褄を合わせようとして必ず外すため、置かない。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 案件あたりの待ち合計営業日を0〜p90のスケールで描き、p90を主指標・中央値を副指標として並べる
 * @graph-connects waitMedianRatio [calls] トラック上の中央値位置の算出
 */
export function WaitTimePanel({ waitTotal }: WaitTimePanelProps) {
  const { t } = useTranslation();

  if (!waitTotal) {
    return null;
  }

  const { days } = waitTotal;
  const waitColor = PMO_FLOW_CLASS_COLORS.wait;
  const trackStyle: CSSProperties = {
    backgroundColor: `color-mix(in srgb, ${waitColor} 22%, transparent)`,
  };
  const fillStyle: CSSProperties = {
    backgroundColor: waitColor,
    width: `${String(waitMedianRatio(days) * 100)}%`,
  };
  const markerStyle: CSSProperties = {
    backgroundColor: waitColor,
    left: `${String(waitMedianRatio(days) * 100)}%`,
    width: `${String(MARKER_WIDTH_PX)}px`,
  };

  return (
    <div className="mt-3" data-testid="pmo-flow-wait-panel">
      <div className="mb-1">
        <span
          className="text-xs font-medium text-[var(--color-text-secondary)]"
          title={t('kpi.pmoFlow.wait.hint')}
        >
          {t('kpi.pmoFlow.wait.title')}
        </span>
      </div>

      <div
        className={`relative w-full overflow-hidden rounded-full ${TRACK_HEIGHT_CLASS}`}
        style={trackStyle}
        role="img"
        aria-label={t('kpi.pmoFlow.wait.barLabel', { p50: days.p50, p90: days.p90 })}
      >
        <div className="h-full rounded-full" style={fillStyle} />
        <span aria-hidden="true" className="absolute inset-y-0" style={markerStyle} />
      </div>

      <div className="mt-1 flex items-baseline justify-between gap-3">
        <span className="text-[11px] text-[var(--color-text-muted)]">
          {t('kpi.pmoFlow.wait.median')}{' '}
          <span className="font-medium tabular-nums text-[var(--color-text-secondary)]">
            {days.p50}
          </span>
          {t('kpi.pmoFlow.unit.daysSuffix')}
        </span>
        {/* p90 が主指標。待ちは右裾が長く、p50 は既に底に張り付いていて改善余地が読めない */}
        <span
          className="text-[11px] text-[var(--color-text-muted)]"
          title={t('kpi.pmoFlow.wait.p90Hint')}
        >
          {t('kpi.pmoFlow.wait.p90')}{' '}
          <span className="text-sm font-semibold tabular-nums text-[var(--color-text-primary)]">
            {days.p90}
          </span>
          {t('kpi.pmoFlow.unit.daysSuffix')}
        </span>
      </div>
    </div>
  );
}
